import React from 'react';

import BookNewCoachingSessionSidebar, {
  SelfScheduleOptions,
} from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CoachingConstants from 'coaching/CoachingConstants';
import CurrentUser from 'auth/CurrentUser';
import ManageRecurringSessionSidebar from 'coaching/components/CoachingHomeView/ManageRecurringSessionSidebar';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import MediaObject from 'components/MediaObject';
import { getCoachTitle } from 'coaching/lib/nameFormat';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';
import { useCoachingViewState } from 'coaching/components/CoachingView';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';
import { TimeslotFrequencyInput } from 'globalTypes';
import {
  CoachMetadata,
  CoachingSessionSchedule,
  CoachingSessionInstance,
  CoachingSessionTools,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import { OrganizationTutoringMetadata } from 'utils/coachingOrganizationHelpers';

import 'coaching/components/styles/CoachingSessionsBySubjectContainer.scss';

const userIsOnDesktop = userAgent.isDesktop();

type Props = {
  subjectName?: string;
  recurringSessionSchedules?: CoachingSessionSchedule[];
  upcomingInstances?: CoachingSessionInstance[];
  children: React.ReactNode;
  sessionTools?: CoachingSessionTools;
  defaultCoach?: CoachMetadata;
  participantUuid: string;
  organizationTutoringMetadata: OrganizationTutoringMetadata;
  selfScheduleOptions: SelfScheduleOptions;
};

export default function CoachingSessionsBySubjectBox(props: Props) {
  return (
    <Row>
      <Box noShadow textAlign="left" bandHeader={<SubjectHeader {...props} />}>
        <BoxBody full flush divided={true}>
          {props.children}
        </BoxBody>
      </Box>
    </Row>
  );
}

function SubjectHeader({
  subjectName,
  recurringSessionSchedules,
  upcomingInstances,
  sessionTools,
  defaultCoach,
  participantUuid,
  organizationTutoringMetadata,
  selfScheduleOptions,
}: Props) {
  if (!subjectName) return null;
  return (
    <MediaObject figure="right" gutter="small">
      <SubjectTextRow
        recurringSessionSchedules={recurringSessionSchedules}
        subjectName={subjectName}
      />
      <Buttons reset="none">
        {!organizationTutoringMetadata.enrolledInOrganizationTutoring &&
          upcomingInstances &&
          upcomingInstances.length > 0 &&
          defaultCoach &&
          sessionTools && (
            <AddSessionButton
              subjectName={subjectName}
              defaultCoach={defaultCoach}
              sessionTools={sessionTools}
              hasExistingRecurringSchedule={Boolean(
                recurringSessionSchedules && recurringSessionSchedules.length > 0
              )}
              participantUuid={participantUuid}
              selfScheduleOptions={selfScheduleOptions}
              organizationTutoring={organizationTutoringMetadata}
            />
          )}
        {!organizationTutoringMetadata.enrolledInOrganizationTutoring &&
          recurringSessionSchedules && (
            <ManageSessionButton
              subjectName={subjectName}
              defaultCoach={defaultCoach}
              sessionTools={sessionTools}
              recurringSessionSchedules={recurringSessionSchedules}
              participantUuid={participantUuid}
              upcomingInstances={upcomingInstances}
              selfScheduleOptions={selfScheduleOptions}
              organizationTutoring={organizationTutoringMetadata}
            />
          )}
      </Buttons>
    </MediaObject>
  );
}

function SubjectTextRow({
  recurringSessionSchedules,
  subjectName,
}: {
  recurringSessionSchedules?: CoachingSessionSchedule[];
  subjectName: string;
}) {
  const cadenceInformation = calculateCadenceInformation(recurringSessionSchedules);
  const coachNameString = Boolean(recurringSessionSchedules?.length)
    ? `with ${recurringSessionSchedules?.map(s => getCoachTitle(s.coach.name)).join(', ')}`
    : undefined;
  const textComponent = (
    <Text weight="semibold" size="medium">
      {userIsOnDesktop ? `${subjectName} ${coachNameString ?? ''}` : subjectName}
    </Text>
  );
  return userIsOnDesktop ? (
    <MediaObject center gutter="small">
      {textComponent}
      {cadenceInformation}
    </MediaObject>
  ) : (
    <>
      <Row size="xxsmall">{textComponent}</Row>
      {cadenceInformation}
    </>
  );
}

function hasMultipleRecurringSessionsFeature() {
  const currentUser = CurrentUser.getUser();
  return Boolean(currentUser && currentUser.hasFeature('add_recurring_sessions.web.dec-21'));
}

function getDefaultStepForSidebar(hasExistingRecurringSchedule: boolean) {
  if (hasMultipleRecurringSessionsFeature()) {
    return 'frequency-selection';
  } else if (hasExistingRecurringSchedule) {
    return 'book-session';
  } else {
    return 'frequency-selection';
  }
}

function getShowFrequencySelect(hasExistingRecurringSchedule: boolean) {
  if (hasMultipleRecurringSessionsFeature()) {
    return true;
  } else if (hasExistingRecurringSchedule) {
    return false;
  } else {
    return true;
  }
}

function getAddSessionCTA() {
  if (hasMultipleRecurringSessionsFeature()) {
    return userIsOnDesktop ? 'Add sessions' : 'Add';
  } else {
    return userIsOnDesktop ? 'Add session' : 'Add';
  }
}

function AddSessionButton({
  subjectName,
  defaultCoach,
  participantUuid,
  hasExistingRecurringSchedule,
  sessionTools,
  selfScheduleOptions,
  organizationTutoring,
}: {
  subjectName: string;
  defaultCoach: CoachMetadata;
  participantUuid: string;
  hasExistingRecurringSchedule: boolean;
  sessionTools: CoachingSessionTools;
  selfScheduleOptions: SelfScheduleOptions;
  organizationTutoring: OrganizationTutoringMetadata;
}) {
  const { closeSidebar, setSidebar } = useCoachingViewState();

  return (
    <Button
      eventKey={`${coachingHomeEventPrefix}.add_session.click`}
      color="mascot"
      data-test="subject-header-add-session-button"
      level="icon"
      onClick={() => {
        setSidebar(
          <BookNewCoachingSessionSidebar
            subjectName={subjectName}
            coach={defaultCoach}
            participantUuid={participantUuid}
            defaultFrequency={TimeslotFrequencyInput.once}
            defaultStep={getDefaultStepForSidebar(hasExistingRecurringSchedule)}
            sessionTools={sessionTools}
            onClose={closeSidebar}
            showFrequencySelect={getShowFrequencySelect(hasExistingRecurringSchedule)}
            selfScheduleOptions={selfScheduleOptions}
            organizationTutoring={organizationTutoring}
          />
        );
      }}>
      <TextWithIcon icon="+" size="small" weight="semibold" data-test="add-sessions-cta">
        {getAddSessionCTA()}
      </TextWithIcon>
    </Button>
  );
}

function ManageSessionButton({
  subjectName,
  defaultCoach,
  participantUuid,
  recurringSessionSchedules,
  sessionTools,
  upcomingInstances,
  selfScheduleOptions,
  organizationTutoring,
}: {
  subjectName: string;
  defaultCoach?: CoachMetadata;
  participantUuid: string;
  recurringSessionSchedules: CoachingSessionSchedule[];
  sessionTools?: CoachingSessionTools;
  upcomingInstances?: CoachingSessionInstance[];
  selfScheduleOptions: SelfScheduleOptions;
  organizationTutoring: OrganizationTutoringMetadata;
}) {
  const { closeSidebar, setSidebar } = useCoachingViewState();

  return (
    <Button
      color="mascot"
      level="icon"
      data-test="subject-header-manage-plan-button"
      eventKey={`${coachingHomeEventPrefix}.manage_plan.click`}
      onClick={() => {
        setSidebar(
          <ManageRecurringSessionSidebar
            participantUuid={participantUuid}
            recurringCoachingSessionSchedules={recurringSessionSchedules}
            upcomingInstances={upcomingInstances}
            subjectName={subjectName}
            onClose={closeSidebar}
            onClickBookSession={
              sessionTools && defaultCoach
                ? () =>
                    setSidebar(
                      <BookNewCoachingSessionSidebar
                        subjectName={subjectName}
                        sessionTools={sessionTools}
                        coach={defaultCoach}
                        onClose={closeSidebar}
                        participantUuid={participantUuid}
                        selfScheduleOptions={selfScheduleOptions}
                        organizationTutoring={organizationTutoring}
                      />
                    )
                : // We should theoretically never hit this since we'd expect a user who's managing their recurring
                  // sessions to have session tools + a default coach, but including it just to be safe.
                  () =>
                    window.open(
                      `${CoachingConstants.TUTORING_HELP_FORM_ROUTE}?request=c_new_session`,
                      '_blank'
                    )
            }
          />
        );
      }}>
      <TextWithIcon icon="calendar" size="small" weight="semibold">
        {userIsOnDesktop ? 'Edit schedule' : 'Edit'}
      </TextWithIcon>
    </Button>
  );
}

function hasFreeTrial(instances?: CoachingSessionInstance[]) {
  return instances?.some(i => i.isFreeTrial);
}

function calculateCadenceInformation(
  recurringSchedules?: CoachingSessionSchedule[],
  instances?: CoachingSessionInstance[]
) {
  function CadenceText({ children }: { children: React.ReactNode }) {
    return (
      <Text size="xsmall" color="graphite">
        {children}
      </Text>
    );
  }

  if (hasFreeTrial(instances)) {
    return <CadenceText>Free trial session</CadenceText>;
  } else if (recurringSchedules?.length === 1) {
    return <CadenceText>{recurringSchedules[0].description}</CadenceText>;
  } else {
    return null;
  }
}
